"use client";

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ClassNames from "embla-carousel-class-names";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaCarouselType } from "embla-carousel";
import { FeedEvents, trackEvent } from "@/analytics";
import CarouselLeftButton from "@/components/common/carousels/CarouselLeftButton";
import CarouselRightButton from "@/components/common/carousels/CarouselRightButton";
import CarouselLeftGradient from "@/components/common/carousels/CarouselLeftGradient";
import CarouselRightGradient from "@/components/common/carousels/CarouselRightGradient";
import { useImageCenteredArrowOffset } from "@/components/common/carousels/useImageCenteredArrowOffset";

export type SlideData = {
  Component: ReactNode;
  id: string;
};

type Props = {
  title: string;
  slides: SlideData[];
  location: string;
};

const SuggestedComediansCarousel = ({ title, slides, location }: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: false, dragFree: true },
    [ClassNames()]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const [canScrollNext, setCanScrollNext] = useState<boolean>(false);

  // Center the arrows on each slide's image rather than the full card (which
  // also includes the name/label/button text below the image).
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const arrowTop = useImageCenteredArrowOffset(containerRef, trackRef, [
    slides,
  ]);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const newIndex = emblaApi.selectedScrollSnap();

      // Track analytics based on direction - captures ALL scroll methods
      if (newIndex > selectedIndex) {
        trackEvent(FeedEvents.CLICKED_NEXT_SUGGESTED_CAROUSEL, {
          location,
        });
      } else if (newIndex < selectedIndex) {
        trackEvent(FeedEvents.CLICKED_PREVIOUS_SUGGESTED_CAROUSEL, {
          location,
        });
      }

      setSelectedIndex(newIndex);
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    },
    [location, selectedIndex]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  if (!slides || slides.length === 0) return null;

  return (
    <div className="w-full">
      {title && (
        <div className="flex flex-row items-center justify-between mb-3 md:mb-4 px-4 md:px-0">
          <h2 className="font-sans text-slate-gray-900 normal-case font-medium text-[20px] pr-2 truncate">
            {title}
          </h2>
        </div>
      )}
      <div className="relative overflow-hidden" ref={containerRef}>
        <CarouselLeftGradient isVisible={canScrollPrev} />
        <CarouselLeftButton
          onClick={onPrevButtonClick}
          isDisabled={!canScrollPrev}
          style={arrowTop !== null ? { top: arrowTop } : undefined}
        />

        <CarouselRightGradient isVisible={canScrollNext} />
        <CarouselRightButton
          onClick={onNextButtonClick}
          isDisabled={!canScrollNext}
          style={arrowTop !== null ? { top: arrowTop } : undefined}
        />

        <div
          className="overflow-x-auto overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          ref={emblaRef}
        >
          <div
            className="flex flex-row touch-pan-y backface-hidden gap-y-8 gap-x-6 px-4 md:px-0"
            ref={trackRef}
          >
            {slides
              .filter((slide) => slide !== null)
              .map(({ Component, id }) => (
                <React.Fragment key={id}>{Component}</React.Fragment>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestedComediansCarousel;
